import { Anchor, Box, Button, Group, Image, Paper, Stack, Text, Tooltip } from '@mantine/core'
import { IconExternalLink } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { CollapsibleContentSection } from '@/components/CollapsibleContentSection'
// import { EmptyState } from '@/components/EmptyState'
import { TableContentGrid } from '@/components/TableContentGrid'
import { theme } from '@/configs/theme'
import { useGlobalState } from '@/hooks/useGlobalState'
import { UploadedFilePreview } from '@/screens/ContentModuleFormScreen/UploadedFilePreview'
import { useStepResources } from '@/screens/ContentModuleFormScreen/useStepResources'
import type { GetStepsResponse, ResourceType } from '@/services/api/api.types'
import { CONTENT_ACCESS_LABELS, resolveContentAccess, resolvePrimaryAccess } from '@/utils/contentAccess'
import { resolveThumbnailUrl } from '@/utils/thumbnails'

import { useContentModuleScreenData } from './ContentModuleScreen'
import { FAQSectionTable } from './ModuleFAQSection'

type StepSectionProps = {
  step: GetStepsResponse[0]
  index: number
  fileId?: string
  /** The primary's access is resolved, never read back — decision 2, same as the editor. */
  isPrimary?: boolean
}

export function StepSection(props: StepSectionProps) {
  const data = useContentModuleScreenData()

  const { api, configStore } = useGlobalState()

  const stepContentQuery = useQuery({
    queryKey: ['step', 'content', props.step.stepId, props.fileId],
    queryFn: () => api.getContentForPreview({ step_id: props.step.stepId, file_id: props.fileId }),
    enabled: !!props.step.stepId,
  })

  const contentSourceUrl =
    stepContentQuery.data && 'sourceUrl' in stepContentQuery.data ? stepContentQuery.data.sourceUrl : undefined

  const { uploadedFileResource, sourceFileResource } = useStepResources(props.step.stepId, contentSourceUrl)

  // The same resolver the editor writes through, rather than a fourth copy of the rule,
  // so this reports what the editor would save rather than flags it has already decided
  // no longer apply.
  const contentAccess = useMemo(() => {
    if (!sourceFileResource) return null
    const facts = {
      type: sourceFileResource.type,
      showInAppSupported: sourceFileResource.showInAppSupported,
    }
    if (props.isPrimary) return resolvePrimaryAccess({ ...facts, showInApp: sourceFileResource.showInApp })
    return resolveContentAccess({
      ...facts,
      showInApp: sourceFileResource.showInApp,
      useInRag: props.step.useInRag,
    })
  }, [sourceFileResource, props.step.useInRag, props.isPrimary])

  const thumbnailUrl = sourceFileResource ? resolveThumbnailUrl(sourceFileResource, configStore.serverBaseUrl) : null

  const rows = useMemo(() => {
    const result = [
      { heading: 'File Title', content: <Text fw={600}>{props.step.name}</Text> },
      {
        heading: 'Source',
        content: uploadedFileResource ? (
          <UploadedFilePreview
            fileName={uploadedFileResource.fileName || 'Uploaded file'}
            type={uploadedFileResource.type as ResourceType}
            resourceKey={uploadedFileResource.resourceKey}
            resourceData={uploadedFileResource.data}
          />
        ) : sourceFileResource && (sourceFileResource.type === 'url' || sourceFileResource.type === 'youtube') ? (
          sourceFileResource.data || sourceFileResource.sourceUrl ? (
            <Anchor href={sourceFileResource.data || sourceFileResource.sourceUrl || ''} target="_blank" size="sm">
              <Group gap={6}>
                {sourceFileResource.data || sourceFileResource.sourceUrl}
                <IconExternalLink size={14} />
              </Group>
            </Anchor>
          ) : (
            <Text c="dimmed">URL unavailable</Text>
          )
        ) : (
          <Text c="dimmed">No Source File</Text>
        ),
      },
      ...(thumbnailUrl
        ? [
            {
              heading: 'Thumbnail',
              content: (
                <Paper withBorder radius="md" p="sm" bg="gray.1" maw={420}>
                  <Stack gap="sm">
                    <Box
                      style={{
                        width: '100%',
                        aspectRatio: '16 / 9',
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '1px solid var(--mantine-color-gray-3)',
                        backgroundColor: 'var(--mantine-color-gray-2)',
                      }}>
                      <Image
                        src={thumbnailUrl}
                        alt="Thumbnail"
                        w="100%"
                        h="100%"
                        fit="cover"
                        style={{ cursor: 'pointer' }}
                        onClick={() => window.open(thumbnailUrl, '_blank')}
                      />
                    </Box>
                    <Group gap="sm" justify="flex-end">
                      <Tooltip label="Open in new tab">
                        <Button
                          variant="subtle"
                          color="gray"
                          size="compact-sm"
                          rightSection={<IconExternalLink size={16} />}
                          onClick={() => window.open(thumbnailUrl, '_blank')}>
                          Preview
                        </Button>
                      </Tooltip>
                    </Group>
                  </Stack>
                </Paper>
              ),
            },
          ]
        : []),
      // Access is a fact about the file, not about the module's delivery mode — decision 3.
      // Gated on the access resolving at all, not on `showInAppSupported` being true: the
      // backend only stamps that field from C2-2617 on, and the resolver reads a missing
      // one as supported. Testing it here instead hid the row for every older resource.
      ...(contentAccess
        ? [
            {
              heading: 'Content Access',
              content: <Text>{CONTENT_ACCESS_LABELS[contentAccess]}</Text>,
            },
          ]
        : []),
      {
        heading: 'File Description',
        content: sourceFileResource?.description ? (
          <Text style={{ whiteSpace: 'pre-wrap' }}>{sourceFileResource.description}</Text>
        ) : (
          <Text c="dimmed">No Description</Text>
        ),
      },
      {
        heading: 'Extracted Content',
        content: (
          <Box>
            <Paper bg={theme.lighten(theme.colors.primaryColors[1], 0.7)} radius="sm" p="md" pb="xl" shadow="0">
              <Text size="sm" fw={500} lineClamp={6} style={{ whiteSpace: 'pre-wrap' }}>
                {stepContentQuery.data?.content}
              </Text>
            </Paper>
            <Group justify="center">
              <Button
                size="sm"
                style={{ marginTop: 'calc(var(--button-height-sm) / -2)' }}
                onClick={() =>
                  data.modal.current?.openModal({
                    title: 'Previewing Extracted Content',
                    data: stepContentQuery.data?.content,
                    opts: {
                      markdown: true,
                    },
                  })
                }>
                View Full Content
              </Button>
            </Group>
          </Box>
        ),
      },
      // Decision 18: Media Only hides that file's FAQs. The file's access, not the module's.
      ...(contentAccess === 'media_only'
        ? []
        : [
            {
              heading: 'File FAQs',
              content: <FAQSectionTable data={props.step.faqs ?? []} />,
            },
          ]),
    ]
    return result
  }, [
    props.step.name,
    props.step.faqs,
    uploadedFileResource,
    sourceFileResource,
    contentAccess,
    thumbnailUrl,
    stepContentQuery.data,
    data.modal,
  ])

  return (
    <CollapsibleContentSection
      defaultCollapsed
      id={`step-${props.step.stepId}`}
      title={props.step.name ? `File Details: ${props.step.name}` : `File ${props.index + 1} Details`}
      ContentElement={<TableContentGrid headingFlexWidth="264px" rows={rows} />}
    />
  )
}
